// @givmo/board — what waits on the person on one board, read off that board's attention read.
//
// Two places show it (CBG-FIX109's call C1, decided 2026-10-17 21:03 PDT): the home's "Coming up",
// under each board's name, for someone holding two or more seats, in this order: what waits for
// their signature first — a closed session's minutes to sign to approve (CBG-RC10), then written
// consents (CBG-FIX107) — then their getting-started list, the board's self-assessment, the year's
// conflict-of-interest disclosure and a copy of the return to read; and the to-do list beside every
// page of the board (CBG-UX7-WEB-DIR, `director-todo-list.ts`), which the "Waiting on you" card that
// sat at the top of the board's page became (D-CBG-35 (2)), in the list's own order (§4.3).
//
// Each kind is read field by field off `unknown` by its own tolerant reader: the generated union is a
// compile-time claim, a malformed item is left out, and an unknown kind is a newer backend.
//
// ══ MINUTES WAITING WHILE THE SIGNING WINDOW IS SHUT (CBG-FIX74) ═══════════════════════════════════
//
// The RC10 row naming the minutes is withheld while the window is shut (whether someone was in a
// closed session is closed-session content), though the ask's email has told the person "The {Org}
// board has minutes waiting for your signature." So the read carries one fact beside its list,
// `minutes_approval_signature_waiting`, the same whether the window is open or shut, and where the
// same answer names no minutes the RC10 row's place says the email's own words (`minutesWhileShut`).
// Only a read made while the window was shut answers that way: with it open, the RC10 rows name every
// minutes the fact counts. The read decides, never `/me`: a board read while shut stays cached after
// the window opens (another board's confirmation, a consent's Sign), and keying the line on `/me`
// would hide its minutes until that read refreshed (review-238's F2). An answer without the field (a
// backend from before it) reads as false: today's home, through a deploy's overlap.
//
// ══ A WRITTEN CONSENT WAITING FOR THEIR SIGNATURE (CBG-FIX107) ═════════════════════════════════════
//
// The consent's circulation email links home too. The read serves each consent waiting for this
// person's signature (`written_consent`) whatever the window: nothing about a consent is closed-session
// content. One row per board says so in the email's words, never the consent's title, text or date.

import type { OrgBoardDirectorAttentionOut } from '@givmo/api-client';
import { coiDisclosuresAwaiting, type CoiDisclosureAwaiting } from './director-coi';
import { consentDeliveryWords as cd } from '../consents/consent-delivery-words';
import {
  consentsAwaitingDirector,
  type ConsentAwaitingDirector,
} from '../consents/consents-decode';
import {
  selfAssessmentsAwaiting,
  type SelfAssessmentAwaiting,
} from '../evaluations/assessment-decode';
import {
  approvalSignaturesAwaiting,
  type ApprovalSignatureAwaiting,
} from '../minutes/minutes-signatures-decode';
import { returnsToRead, type ReturnToRead } from '../returns/return-decode';

/** What waits on the person on one board, kind by kind, in the list's order. */
export interface BoardWaiting {
  /** CBG-FIX74: minutes wait for their signature, said where the read names none (a shut window's). */
  minutesWhileShut: boolean;
  /** CBG-RC10: a closed session's minutes they were asked to sign (served while signing is open). */
  approvals: ApprovalSignatureAwaiting[];
  /** CBG-FIX107: written consents waiting for their signature, the window open or shut. */
  consents: ConsentAwaitingDirector[];
  /** CBG-YR4-START: documents on their getting-started list still to read; 0 when none. */
  toRead: number;
  /** CBG-YR4-ASSESS-WEB: the board's open self-assessment, not yet answered. */
  assessments: SelfAssessmentAwaiting[];
  /** CBG-YR2-WEB2: a year's disclosure sent to them, not yet answered. */
  years: CoiDisclosureAwaiting[];
  /** CBG-YR5-DIST: a copy of the return provided to them, not yet opened or read. */
  returns: ReturnToRead[];
  /** EMC-WEB4 (frame 25): a Board Ask sent to them, not yet acted on (shared, or a page started). */
  asks: BoardAskAwaiting[];
}

/** A Board Ask waiting on this seat (the attention read's `board_ask`). */
export interface BoardAskAwaiting {
  askId: string;
  campaignName: string;
  offersShare: boolean;
  offersPage: boolean;
  fromDisplayName: string | null;
}

/** The attention read's `board_ask` items, read tolerantly: a malformed one is left out. */
export function boardAsksAwaiting(items: unknown): BoardAskAwaiting[] {
  if (!Array.isArray(items)) return [];
  const out: BoardAskAwaiting[] = [];
  for (const raw of items) {
    if (typeof raw !== 'object' || raw === null) continue;
    const item = raw as Record<string, unknown>;
    if (item.kind !== 'board_ask') continue;
    if (typeof item.ask_id !== 'string' || typeof item.campaign_name !== 'string') continue;
    out.push({
      askId: item.ask_id,
      campaignName: item.campaign_name,
      offersShare: item.offers_share !== false,
      offersPage: item.offers_page !== false,
      fromDisplayName: typeof item.from_display_name === 'string' ? item.from_display_name : null,
    });
  }
  return out;
}

/** The getting-started item's count of documents still to read: one item per board, 0 for none. */
function toReadOf(items: unknown): number {
  if (!Array.isArray(items)) return 0;
  for (const raw of items) {
    if (typeof raw !== 'object' || raw === null) continue;
    const item = raw as Record<string, unknown>;
    if (item.kind !== 'getting_started') continue;
    const count = Number(item.to_read_count);
    return Number.isInteger(count) && count > 0 ? count : 0;
  }
  return 0;
}

/** CBG-FIX74's fact, read tolerantly: anything but `true` (an older backend's absence) is false. */
function minutesWaitingOf(attention: unknown): boolean {
  return (
    typeof attention === 'object' &&
    attention !== null &&
    (attention as Record<string, unknown>).minutes_approval_signature_waiting === true
  );
}

/** What waits on the person on this board; nothing while its read hasn't answered. */
export function waitingOn(attention: OrgBoardDirectorAttentionOut | undefined): BoardWaiting {
  const items: unknown = attention?.items;
  const approvals = approvalSignaturesAwaiting(items);
  return {
    // Never beside an RC10 row; and by the read alone, never `/me` (see the header).
    minutesWhileShut: approvals.length === 0 && minutesWaitingOf(attention),
    approvals,
    consents: consentsAwaitingDirector(items),
    toRead: toReadOf(items),
    assessments: selfAssessmentsAwaiting(items),
    years: coiDisclosuresAwaiting(items),
    returns: returnsToRead(Array.isArray(items) ? items : null),
    asks: boardAsksAwaiting(items),
  };
}

/** Whether nothing waits: no row at all, so a board's page shows no card. */
export function nothingWaits(waiting: BoardWaiting): boolean {
  return (
    !waiting.minutesWhileShut &&
    waiting.approvals.length === 0 &&
    waiting.consents.length === 0 &&
    waiting.toRead === 0 &&
    waiting.assessments.length === 0 &&
    waiting.years.length === 0 &&
    waiting.returns.length === 0 &&
    waiting.asks.length === 0
  );
}

/**
 * CBG-FIX107: the consents row's line. One consent of a committee's is named by its committee, as its
 * meetings are on the list; on the page of a seat on that one committee alone it goes unsaid, as on
 * its meetings there (the page's own line names the committee). Several count themselves.
 */
export function consentsLine(
  consents: readonly ConsentAwaitingDirector[],
  { committeeSaid }: { committeeSaid: boolean },
): string {
  const [only] = consents;
  return consents.length === 1 && only?.committeeName != null && committeeSaid
    ? cd.homeWaitingCommittee(only.committeeName)
    : cd.homeWaiting(consents.length);
}
